import type { EventBody, ServeResponse } from "./types";

// resolveURL accepts either an absolute URL (cdn host) or a path relative
// to the current page origin. Returning a URL object lets callers tweak
// the query string without manual string concatenation.
function resolveURL(base: string, suffix: string): URL {
  return new URL(base + suffix, window.location.href);
}

export interface FetchAdOptions {
  base: string;
  key: string;
  slot: string;
  user: string;
  fmt?: string;
  refreshSeq?: number;
  gdpr?: 0 | 1;
  consentString?: string;
  signal?: AbortSignal;
}

// fetchAd performs the /serve call. Always returns a parsed ServeResponse
// or throws. The backend never returns 4xx/5xx for content reasons
// (HOUSE fallback covers all failure modes), so any non-2xx here is a
// transport/auth error worth surfacing to the caller for telemetry.
export async function fetchAd(opts: FetchAdOptions): Promise<ServeResponse> {
  const url = resolveURL(opts.base, "serve");
  url.searchParams.set("slot", opts.slot);
  if (opts.user) url.searchParams.set("u", opts.user);
  if (opts.fmt) url.searchParams.set("fmt", opts.fmt);
  if (opts.refreshSeq) url.searchParams.set("rq", String(opts.refreshSeq));
  if (opts.gdpr !== undefined) url.searchParams.set("gdpr", String(opts.gdpr));
  const headers: Record<string, string> = { "X-AdPluga-Key": opts.key };
  if (opts.consentString) headers["X-Consent-String"] = opts.consentString;

  const resp = await fetch(url.toString(), {
    method: "GET",
    headers,
    mode: "cors",
    credentials: "omit",
    cache: "no-store",
    signal: opts.signal,
  });
  if (!resp.ok) throw new Error("serve_status_" + resp.status);
  return (await resp.json()) as ServeResponse;
}

// trackEvent fires a /track POST. Fire-and-forget: errors are swallowed
// because there is no UX value in surfacing them. `keepalive: true` lets
// the request survive page unload (impression at the end of a session).
// fireBeacon sends a fire-and-forget GET to a third-party notice URL (OpenRTB
// nurl/burl). `no-cors` because we never read the response and the bidder host
// will not send CORS headers; `keepalive` lets it survive page unload.
export function fireBeacon(url: string): void {
  if (!url) return;
  try {
    void fetch(url, { method: "GET", mode: "no-cors", keepalive: true, cache: "no-store" }).catch(() => {});
  } catch {
    // Synchronous throw (CSP) is intentionally muted.
  }
}

export function trackEvent(base: string, body: EventBody): void {
  const url = resolveURL(base, "track").toString();
  try {
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      mode: "cors",
      credentials: "omit",
      keepalive: true,
      cache: "no-store",
    }).catch(() => {});
  } catch {
    // Any synchronous throw (e.g. CSP refusal) is intentionally muted.
  }
}
